import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { 
  Search, Sparkles, X, RefreshCw, Phone, MessageSquare, 
  FileText, Mail, Calendar, User, MapPin, ChevronRight,
  Zap, Brain, Clock, Filter, Mic
} from "lucide-react";
import ChristelleVoicePopup from "./ChristelleVoicePopup";
import getApiUrl from './utils/getApiUrl';

const API = `${getApiUrl()}/api`;

/**
 * 🔍 Composant de Recherche Intelligente IA "Christelle"
 * Recherche sémantique et assistée dans toutes les données client
 */
export default function ChristelleSearch({ onClientClick, onClose }) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [interpretation, setInterpretation] = useState("");
  const [searchTime, setSearchTime] = useState(0);
  const [sourcesSearched, setSourcesSearched] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showVoicePopup, setShowVoicePopup] = useState(false);
  
  // Filtres
  const [filters, setFilters] = useState({
    include_calls: true,
    include_sms: true,
    include_sheet: true,
    include_emails: false,
    include_documents: false
  });
  const [showFilters, setShowFilters] = useState(false);
  
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  
  // Focus sur l'input au montage
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  
  // Fetch suggestions
  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    try {
      const res = await axios.get(`${API}/christelle/suggest`, { params: { q } });
      if (res.data.success && res.data.suggestions.length > 0) {
        setSuggestions(res.data.suggestions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (err) {
      console.error("Erreur suggestions:", err);
    }
  }, []);
  
  // Debounced suggestions
  const handleQueryChange = (value) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };
  
  // Exécuter la recherche
  const executeSearch = async (searchQuery = query) => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    setShowSuggestions(false);
    setResults([]);
    setInterpretation("");
    
    try {
      const res = await axios.post(`${API}/christelle/search`, {
        query: searchQuery,
        ...filters,
        max_results: 20
      });
      
      if (res.data.success) {
        setResults(res.data.results || []);
        setInterpretation(res.data.interpretation || "");
        setSearchTime(res.data.search_time_ms || 0);
        setSourcesSearched(res.data.sources_searched || []);
      }
    } catch (err) {
      console.error("Erreur recherche:", err);
    }
    
    setLoading(false);
  };
  
  // Gérer la touche Enter
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      executeSearch();
    } else if (e.key === "Escape") {
      onClose?.();
    }
  };
  
  // Sélectionner une suggestion
  const selectSuggestion = (suggestion) => {
    setQuery(suggestion);
    setShowSuggestions(false);
    executeSearch(suggestion);
  };
  
  // Cliquer sur un résultat
  const handleResultClick = (result) => {
    if (result.row_number && onClientClick) {
      onClientClick(result.row_number, result.client_id);
    }
  };
  
  // Icône selon la source
  const getSourceIcon = (source) => {
    switch (source) {
      case "call": return <Phone className="w-4 h-4" />;
      case "sms": return <MessageSquare className="w-4 h-4" />;
      case "sheet": return <FileText className="w-4 h-4" />;
      case "email": return <Mail className="w-4 h-4" />;
      case "document": return <FileText className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };
  
  // Couleur selon la source
  const getSourceColor = (source) => {
    switch (source) {
      case "call": return "text-green-400 bg-green-400/10";
      case "sms": return "text-blue-400 bg-blue-400/10";
      case "sheet": return "text-purple-400 bg-purple-400/10";
      case "email": return "text-amber-400 bg-amber-400/10";
      case "document": return "text-slate-400 bg-slate-400/10";
      default: return "text-slate-400 bg-slate-400/10";
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/95 backdrop-blur-sm z-50 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 border-b border-purple-500/30">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  Recherche IA Christelle
                  <Sparkles className="w-5 h-5 text-amber-400" />
                </h1>
                <p className="text-sm text-purple-300">Recherche intelligente dans toutes les données</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-slate-800/50 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
              data-testid="christelle-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Barre de recherche */}
          <div className="relative">
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => handleQueryChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Posez votre question... Ex: 'clients mécontents du SAV', 'problème chantier', 'demande remboursement'"
                  className="w-full pl-12 pr-4 py-4 bg-slate-800/80 border border-purple-500/30 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg"
                  data-testid="christelle-search-input"
                />
                {loading && (
                  <RefreshCw className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400 animate-spin" />
                )}
              </div>
              
              {/* Bouton filtres */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-4 rounded-xl border transition-colors ${
                  showFilters 
                    ? "bg-purple-600 border-purple-500 text-white" 
                    : "bg-slate-800/80 border-purple-500/30 text-slate-400 hover:text-white hover:border-purple-500/50"
                }`}
                data-testid="christelle-filter-btn"
              >
                <Filter className="w-5 h-5" />
              </button>
              
              {/* Bouton micro vocal */}
              <button
                onClick={() => setShowVoicePopup(true)}
                className="p-4 rounded-xl border bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 border-amber-500/50 text-white transition-all shadow-lg shadow-amber-500/20"
                data-testid="christelle-voice-btn"
                title="Recherche vocale"
              >
                <Mic className="w-5 h-5" />
              </button>
              
              {/* Bouton rechercher */}
              <button
                onClick={() => executeSearch()}
                disabled={loading || !query.trim()}
                className="px-6 py-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-white font-medium flex items-center gap-2 transition-all"
                data-testid="christelle-search-btn"
              >
                <Zap className="w-5 h-5" />
                Rechercher
              </button>
            </div>
            
            {/* Suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-2 bg-slate-800 border border-purple-500/30 rounded-xl shadow-2xl overflow-hidden">
                {suggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => selectSuggestion(suggestion)}
                    className="w-full px-4 py-3 text-left text-white hover:bg-purple-600/30 transition-colors border-b border-slate-700 last:border-b-0 flex items-center gap-3"
                  >
                    <Search className="w-4 h-4 text-purple-400" />
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Filtres */}
          {showFilters && (
            <div className="mt-4 p-4 bg-slate-800/50 rounded-xl border border-purple-500/20">
              <p className="text-sm text-slate-400 mb-3">Sources à rechercher :</p>
              <div className="flex flex-wrap gap-3">
                {[
                  { key: "include_sheet", label: "Fiches clients", icon: FileText, color: "purple" },
                  { key: "include_calls", label: "Appels (transcriptions)", icon: Phone, color: "green" },
                  { key: "include_sms", label: "SMS", icon: MessageSquare, color: "blue" },
                  { key: "include_emails", label: "Emails (bientôt)", icon: Mail, color: "amber", disabled: true },
                  { key: "include_documents", label: "Documents (bientôt)", icon: FileText, color: "slate", disabled: true },
                ].map(({ key, label, icon: Icon, color, disabled }) => (
                  <button
                    key={key}
                    onClick={() => !disabled && setFilters({ ...filters, [key]: !filters[key] })}
                    disabled={disabled}
                    className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-all ${
                      disabled 
                        ? "bg-slate-700/50 text-slate-500 cursor-not-allowed"
                        : filters[key]
                          ? `bg-${color}-500/20 text-${color}-400 border border-${color}-500/50`
                          : "bg-slate-700/50 text-slate-400 border border-slate-600 hover:border-slate-500"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Contenu principal */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-5xl mx-auto px-6 py-6">
          {/* Message d'interprétation IA */}
          {interpretation && (
            <div className="mb-6 p-4 bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/30 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Brain className="w-4 h-4 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-purple-300 mb-1">Interprétation de Christelle :</p>
                  <p className="text-white">{interpretation}</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Stats de recherche */}
          {results.length > 0 && (
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4 text-sm text-slate-400">
                <span className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  {results.length} résultat{results.length > 1 ? "s" : ""}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {searchTime}ms
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                {sourcesSearched.map((source, idx) => (
                  <span key={idx} className="px-2 py-1 bg-slate-800 rounded">
                    {source}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Résultats */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mb-4 animate-pulse">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <p className="text-slate-400">Christelle analyse vos données...</p>
            </div>
          ) : results.length > 0 ? (
            <div className="space-y-3">
              {results.map((result, idx) => (
                <div
                  key={idx}
                  onClick={() => handleResultClick(result)}
                  className={`p-4 bg-slate-800/50 border border-slate-700 rounded-xl hover:border-purple-500/50 transition-all ${
                    result.row_number ? "cursor-pointer hover:bg-slate-800" : ""
                  }`}
                  data-testid={`christelle-result-${idx}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Badge source */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getSourceColor(result.source)}`}>
                      {getSourceIcon(result.source)}
                    </div>
                    
                    {/* Contenu */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium text-white">{result.client_name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${getSourceColor(result.source)}`}>
                          {result.source_label}
                        </span>
                        {result.relevance_score > 0 && (
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            result.relevance_score >= 80 ? 'bg-green-500/20 text-green-400' :
                            result.relevance_score >= 50 ? 'bg-amber-500/20 text-amber-400' :
                            'bg-slate-500/20 text-slate-400'
                          }`}>
                            {result.relevance_score}% pertinent
                          </span>
                        )}
                      </div>
                      
                      {/* Champs correspondants */}
                      {result.matched_fields && result.matched_fields.length > 0 && (
                        <div className="flex items-center gap-1 mb-2 flex-wrap">
                          <span className="text-xs text-slate-500">Trouvé dans:</span>
                          {result.matched_fields.map((field, fidx) => (
                            <span key={fidx} className="text-xs px-1.5 py-0.5 bg-purple-500/10 text-purple-300 rounded">
                              {field}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Contenu correspondant */}
                      <p 
                        className="text-sm text-slate-300 mb-2 line-clamp-2"
                        dangerouslySetInnerHTML={{ 
                          __html: result.matched_content?.replace(/\*\*(.*?)\*\*/g, '<mark class="bg-purple-500/30 text-purple-200 px-1 rounded">$1</mark>') || ""
                        }}
                      />
                      
                      {/* Métadonnées */}
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        {result.date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {result.date}
                          </span>
                        )}
                        {result.context && (
                          <span>{result.context}</span>
                        )}
                        {result.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {result.phone}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Flèche si cliquable */}
                    {result.row_number && (
                      <ChevronRight className="w-5 h-5 text-slate-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : query && !loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Search className="w-12 h-12 text-slate-600 mb-4" />
              <p className="text-slate-400">Aucun résultat trouvé pour "{query}"</p>
              <p className="text-sm text-slate-500 mt-2">Essayez avec d'autres mots-clés</p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center mb-6">
                <Sparkles className="w-10 h-10 text-purple-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Recherche Intelligente</h2>
              <p className="text-slate-400 text-center max-w-md mb-6">
                Posez votre question en langage naturel. Christelle cherchera dans les fiches clients, 
                les transcriptions d'appels, les SMS et plus encore.
              </p>
              
              {/* Exemples de recherches intelligentes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 w-full max-w-2xl">
                {[
                  { query: "Clients mécontents", icon: "😤", desc: "Réclamations, problèmes" },
                  { query: "Dossiers bloqués", icon: "🚫", desc: "En attente, pas signés" },
                  { query: "Problèmes SAV", icon: "🔧", desc: "Service après-vente" },
                  { query: "Clients à rappeler", icon: "📞", desc: "Rappels en attente" },
                  { query: "Chantiers en cours", icon: "🏗️", desc: "Travaux démarrés" },
                  { query: "RDV cette semaine", icon: "📅", desc: "Rendez-vous planifiés" }
                ].map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setQuery(item.query);
                      executeSearch(item.query);
                    }}
                    className="px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-left hover:border-purple-500/50 hover:bg-slate-800 transition-all group"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{item.icon}</span>
                      <span className="font-medium text-white group-hover:text-purple-300">{item.query}</span>
                    </div>
                    <p className="text-xs text-slate-500">{item.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Popup vocal */}
      <ChristelleVoicePopup
        isOpen={showVoicePopup}
        onClose={() => setShowVoicePopup(false)}
        onResultClick={(rowNumber, clientId) => {
          setShowVoicePopup(false);
          onClientClick?.(rowNumber, clientId);
        }}
      />
    </div>
  );
}
